import React, { useEffect, useRef, useState } from "react";
import { Select, Spin, Tag } from "antd";
import { getPartners } from "../../../services/packages-service";

const SEARCH_DEBOUNCE_MS = 300;

const toOption = (partner) => ({
  value: partner.id,
  label: partner.displayName,
  partner,
});

/**
 * Searchable partner picker for a listing's organizer_id. Searches
 * server-side (business/individual accounts only). Clearing it sets the
 * value to null, which unassigns the organizer.
 */
const OrganizerSelect = ({ value, onChange, placeholder = "No organizer (Tramango listing)" }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const latestSearchRef = useRef(0);

  const load = async (search = "") => {
    const requestId = ++latestSearchRef.current;
    setLoading(true);
    setError(null);
    try {
      const { partners } = await getPartners({ search: search || undefined, limit: 50 });
      // Ignore responses that arrive after a newer search was fired.
      if (requestId === latestSearchRef.current) setOptions(partners.map(toOption));
    } catch (err) {
      if (requestId === latestSearchRef.current) setError(err.message);
    } finally {
      if (requestId === latestSearchRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (search) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(search), SEARCH_DEBOUNCE_MS);
  };

  // An organizer assigned earlier may not be in the current page of results
  // — keep it selectable/visible rather than showing a bare id.
  const hasValueOption = !value || options.some((o) => o.value === value);
  const allOptions = hasValueOption
    ? options
    : [{ value, label: `Assigned partner (${value.slice(-6)})` }, ...options];

  return (
    <Select
      showSearch
      allowClear
      style={{ width: "100%" }}
      placeholder={placeholder}
      value={value || undefined}
      filterOption={false}
      onSearch={handleSearch}
      onChange={(next) => onChange(next ?? null)}
      onClear={() => load()}
      notFoundContent={
        loading ? <Spin size="small" /> : error ? `Couldn't load partners: ${error}` : "No partners found"
      }
      // The dropdown row shows email + account type; the closed select shows
      // just the name (the Option's `label`).
      optionLabelProp="label"
    >
      {allOptions.map(({ value: id, label, partner }) => (
        <Select.Option key={id} value={id} label={label}>
          {partner ? (
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span>
                {partner.displayName}
                <span style={{ color: "#8c8c8c", marginLeft: 8, fontSize: 12 }}>{partner.email}</span>
              </span>
              <Tag style={{ marginRight: 0 }}>{partner.accountType}</Tag>
            </div>
          ) : (
            label
          )}
        </Select.Option>
      ))}
    </Select>
  );
};

export default OrganizerSelect;
